// SuperuserLibraryBar.jsx — the Library's view, filters and sort, for
// superusers only (V2 #97). Members never see it: their Library is unchanged.
//
// The view (whose cartons) is the backend's to remember and record; this bar
// only asks for a new one through onView. Filters and sort are local choices
// passed up through onFilters — not a view, not recorded.

import React from 'react';
import { useTheme } from './theme';
import { KINDS, SCOPES, SORTS, STATUSES, normaliseView } from './superuserLibrary';

export default function SuperuserLibraryBar({ view, orgs, filters, onView, onFilters, busy }) {
  const { P, FN, MO, inp } = useTheme();
  const v = normaliseView(view);
  // "One organisation" chosen but no organisation picked yet: nothing is asked
  // of the backend until one is (it refuses the view without one).
  const [picking, setPicking] = React.useState(false);
  const scope = picking ? 'org' : v.scope;

  const seg = (on) => ({
    fontFamily: MO, fontSize: 10.5, padding: '5px 10px', borderRadius: 6, cursor: busy ? 'default' : 'pointer',
    border: `0.5px solid ${on ? P.accent : P.rule}`, background: on ? P.accentSoft : 'transparent',
    color: on ? P.accent : P.muted,
  });
  const label = { display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FN, fontSize: 11.5, color: P.muted, cursor: 'pointer' };
  const select = { ...inp, width: 'auto', padding: '5px 8px' };

  const choose = (next) => {
    if (next === 'org') { setPicking(true); return; }
    setPicking(false);
    onView({ scope: next, org_id: null, include_drafts: v.include_drafts });
  };

  return (
    <div style={{
      background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 10, padding: '12px 14px',
      marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.2, textTransform: 'uppercase', color: P.accent, marginRight: 4 }}>Showing</span>
        {SCOPES.map((s) => (
          <button key={s.value} disabled={busy} onClick={() => choose(s.value)} style={seg(scope === s.value)}>{s.label}</button>
        ))}
        {scope === 'org' && (
          <select value={picking && !v.org_id ? '' : (v.org_id || '')} disabled={busy}
            onChange={(e) => {
              if (!e.target.value) return;
              setPicking(false);
              onView({ scope: 'org', org_id: e.target.value, include_drafts: v.include_drafts });
            }} style={select}>
            <option value="">Choose an organisation…</option>
            {(orgs || []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}{o.is_active === false ? ' (inactive)' : ''}</option>
            ))}
          </select>
        )}
        <label style={{ ...label, opacity: v.scope === 'mine' ? 0.5 : 1, marginLeft: 6 }}
          title={v.scope === 'mine' ? 'Your own drafts are always shown' : ''}>
          <input type="checkbox" checked={v.include_drafts} disabled={busy || v.scope === 'mine'}
            onChange={(e) => onView({ ...v, include_drafts: e.target.checked })} />
          Include other people’s drafts
        </label>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <select value={filters.status} onChange={(e) => onFilters({ status: e.target.value })} style={select} aria-label="Status">
          {STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filters.kind} onChange={(e) => onFilters({ kind: e.target.value })} style={select} aria-label="Kind">
          {KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filters.sort} onChange={(e) => onFilters({ sort: e.target.value })} style={select} aria-label="Sort">
          {SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label style={label}>
          <input type="checkbox" checked={filters.duplicatesOnly}
            onChange={(e) => onFilters({ duplicatesOnly: e.target.checked })} />
          Duplicates only
        </label>
        {filters.owner && (
          <button onClick={() => onFilters({ owner: null, ownerName: '' })} title="Show every owner again" style={{
            fontFamily: MO, fontSize: 10.5, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
            border: `0.5px solid ${P.accentLine}`, background: P.accentSoft, color: P.accent,
          }}>Owner: {filters.ownerName || 'one person'} ×</button>
        )}
      </div>

      <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, lineHeight: 1.4 }}>
        Only superusers see this bar. Your choice of whose cartons to show is remembered, and each change is recorded.
        Click an owner’s name on a card to show only their cartons.
      </div>
    </div>
  );
}
